import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Shield, CheckCircle, Phone, MessageCircle, Users, TrendingUp, Star, Target, Heart, Calendar, AlertTriangle, XCircle, ArrowRight, Zap, Award, BadgeCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const PHONE = "7410121121";
const WHATSAPP_LINK = `https://wa.me/91${PHONE}?text=Hi,%20I%20want%20to%20get%20my%20LIC%20policy%20reviewed.`;

const testimonials = [
  {
    quote: "I was shocked to discover how poor my LIC returns were. Their advisor legally restructured my plan and saved me ₹2 lakhs. No one had ever explained this to me before.",
    name: "Ramesh Sharma",
    location: "Pune · Pension Plan · 9 Years",
    outcome: "💰 Saved ₹2,00,000",
    avatar: "RS",
  },
  {
    quote: "They told me surrendering my policy made sense — even though they earned nothing from it. That's when I knew these people are different. Finally, advice I can trust.",
    name: "Sunita Mehta",
    location: "Mumbai · Endowment Plan · 7 Years",
    outcome: "🤝 Honest Exit Advice",
    avatar: "SM",
  },
  {
    quote: "For years I paid premiums blindly. Finally someone explained LIC in simple Hindi. No pressure, no spam calls. Just honest advice on WhatsApp when I needed it.",
    name: "Priya Gupta",
    location: "Jaipur · Child Education Plan · 6 Years",
    outcome: "🧠 Clarity Without Pressure",
    avatar: "PG",
  },
  {
    quote: "My agent never told me about surrender value or what I'd actually get. FutureSanchay team helped me understand all my options clearly — continue, modify, or exit.",
    name: "Vikram Patel",
    location: "Ahmedabad · Endowment Plan · 12 Years",
    outcome: "📄 Understood All Options",
    avatar: "VP",
  }
];

const experienceSteps = [
  {
    number: "01",
    title: "Get Your Policy Reviewed",
    description: "Share your existing policy or goals. Our IRDAI certified experts analyze it for free.",
    features: ["3-min form", "Expert review", "100% Free"],
  },
  {
    number: "02", 
    title: "Understand What's Actually Happening",
    description: "Get a clear report: Is your policy good? Are you overpaying? Should you continue or exit?",
    features: ["Clear diagnosis", "No jargon", "Written report"],
  },
  {
    number: "03",
    title: "Make an Informed Decision (Without Pressure)",
    description: "We guide you on next steps — only if it genuinely helps you.",
    features: ["No pressure", "Your choice", "Lifetime support"],
  }
];

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.5 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Scroll Animation Hook
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Typing Effect Component
const TypingText = ({ texts, speed = 100 }) => {
  const [displayText, setDisplayText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentText = texts[textIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setDisplayText(currentText.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
        
        if (charIndex + 1 === currentText.length) {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        setDisplayText(currentText.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
        
        if (charIndex === 0) {
          setIsDeleting(false);
          setTextIndex((textIndex + 1) % texts.length);
        }
      }
    }, isDeleting ? speed / 2 : speed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, textIndex, texts, speed]);

  return (
    <span className="text-sky-500">
      {displayText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

const HomePage = () => {
  const [statsRef, statsVisible] = useScrollAnimation();
  const [problemRef, problemVisible] = useScrollAnimation();
  const [expRef, expVisible] = useScrollAnimation();
  const [testRef, testVisible] = useScrollAnimation();
  const [calcRef, calcVisible] = useScrollAnimation();

  const problemPoints = [
    "Am I getting poor returns?",
    "Am I paying too much premium\nfor too little cover?",
    "Was this policy mis-sold to me?\nI was told something else.",
    "Should I surrender or continue?\nAnd what will it cost me?"
  ];

  return (
    <div data-testid="home-page" className="bg-white overflow-hidden">
      <Helmet>
        <title>FutureSanchay® | Free LIC Policy Review by IRDAI Experts</title>
        <meta name="description" content="Get your LIC policy reviewed FREE by IRDAI-certified experts. Check if your policy is right or wrong for you. 40,000+ families helped. No sales pressure, no spam." />
        <link rel="canonical" href="https://futuresanchay.com/" />
      </Helmet>
      
      {/* Hero Section - Diagnostic Approach */}
      <section className="pt-4 pb-8 md:pt-8 md:pb-12 relative" data-testid="hero-section">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
          {/* Floating particles */}
          <div className="absolute top-20 left-10 w-3 h-3 bg-sky-400 rounded-full animate-float opacity-40"></div>
          <div className="absolute top-40 right-20 w-2 h-2 bg-amber-400 rounded-full animate-float opacity-50" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-40 left-1/3 w-4 h-4 bg-emerald-400 rounded-full animate-float opacity-30" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="container-main relative z-10">
          {/* Trust Stats - Moved UP */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-6 animate-fade-in-down">
            <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-gray-100 rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-all hover:scale-105 animate-bounce-in">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400 animate-rotate-in" style={{animationDelay: `${i * 100}ms`}} />
                ))}
              </div>
              <span className="text-sm font-bold text-gray-800">4.9</span>
              <span className="text-xs text-gray-500">(500+ reviews)</span>
            </div>
            <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-gray-100 rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-all hover:scale-105 animate-bounce-in delay-200">
              <BadgeCheck className="w-5 h-5 text-sky-500 animate-pulse" />
              <span className="text-sm font-medium text-gray-700">IRDAI Certified Experts</span>
            </div>
          </div>

          {/* Main Headline - Diagnostic Style */}
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-4 animate-fade-in-up">
              <span className="block">Many LIC Policies Are</span>
              <span className="block mt-2">
                <TypingText texts={["Bought Without Full Clarity.", "Misunderstood for years.", "Not Reviewed."]} speed={80} />
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-2 animate-fade-in-up delay-300">
              <span className="font-semibold text-gray-800">Don't keep paying blindly.</span> Know whether your LIC policy is actually working for your future.
            </p>
            <p className="text-sm text-gray-500 animate-fade-in-up delay-400">
              40,000+ families helped • ₹2,200 Cr premiums reviewed
            </p>
          </div>

          {/* Primary CTA */}
          <div className="text-center mb-8 animate-fade-in-up delay-500">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-block">
              <Button className="bg-sky-600 hover:bg-sky-700 text-white h-14 md:h-16 px-8 md:px-12 rounded-2xl text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 animate-pulse-glow group" data-testid="hero-cta">
                🔍 Review My LIC Policy Now
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <p className="text-sm text-gray-600 mt-3 animate-fade-in-up delay-600">
              ✔ 100% Free · No sales pressure · IRDAI experts only
            </p>
          </div>

          {/* Diagnosis Card */}
          <div className="max-w-md mx-auto animate-scale-in delay-700">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-6 shadow-2xl relative overflow-hidden hover:scale-105 transition-all duration-500">
              {/* Animated border */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-sky-500 via-emerald-500 to-sky-500 opacity-20 animate-gradient"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-sky-500 rounded-lg flex items-center justify-center animate-wiggle">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white">🔍 Review My LIC Policy Now →</h3>
                </div>
                
                <p className="text-sky-200 text-sm mb-4 animate-fade-in-left delay-800">
                  What would you like to do?
                </p>

                <div className="space-y-3">
                  <a href={`${WHATSAPP_LINK}&text=Hi,%20I%20want%20to%20check%20my%20existing%20LIC%20policy.`} target="_blank" rel="noopener noreferrer">
                    <button className="w-full bg-white hover:bg-sky-50 text-gray-800 rounded-xl px-4 py-3 flex items-center gap-3 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1 group animate-slide-in-bottom delay-900">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:animate-jello">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="text-left flex-1">
                        <span className="font-semibold text-sm block">Check My Existing Policy</span>
                        <span className="text-xs text-gray-500">Is it right or wrong for me?</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-sky-500 group-hover:translate-x-1 transition-all" />
                    </button>
                  </a>
                  
                  <a href={`${WHATSAPP_LINK}&text=Hi,%20I%20need%20help%20choosing%20the%20right%20LIC%20policy.`} target="_blank" rel="noopener noreferrer">
                    <button className="w-full bg-white/10 hover:bg-white/20 text-white rounded-xl px-4 py-3 flex items-center gap-3 border border-white/20 transition-all duration-300 hover:scale-105 hover:-translate-y-1 group animate-slide-in-bottom delay-1000">
                      <div className="w-10 h-10 bg-sky-500/30 rounded-lg flex items-center justify-center group-hover:animate-jello">
                        <Target className="w-5 h-5 text-sky-300" />
                      </div>
                      <div className="text-left flex-1">
                        <span className="font-semibold text-sm block">Help Me Choose Right Policy</span>
                        <span className="text-xs text-sky-200/70">New to LIC? Start here</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - Fear + Curiosity */}
      <section ref={problemRef} className="py-12 bg-gradient-to-b from-slate-900 to-slate-800 relative overflow-hidden" data-testid="problem-section">
        {/* Animated background lines */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-sky-500 to-transparent animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500 to-transparent animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          <div className={`text-center mb-8 ${problemVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-300 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-pulse">
              <AlertTriangle className="w-4 h-4" />
              ⚠️ Important Reality Check Most LIC Buyers Miss
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3">
              Most LIC Buyers <span className="text-red-400">Don't Know</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {problemPoints.map((point, index) => (
              <div 
                key={index}
                className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:-translate-y-2 group ${problemVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center mb-3 group-hover:animate-shake">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <p className="text-white/90 text-sm font-medium whitespace-pre-line">{point}</p>
              </div>
            ))}
          </div>

          <div className={`text-center mt-8 ${problemVisible ? 'animate-fade-in-up delay-600' : 'opacity-0'}`}>
            <p className="text-sky-300 text-lg font-medium mb-4">
              Don't stay in the dark. Submit in 3 mins, expert reviews in 10 mins.
            </p>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-sky-600 hover:bg-sky-700 text-white h-12 px-8 rounded-xl font-semibold shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110">
                <MessageCircle className="w-5 h-5 mr-2" />
                Get Clarity on My LIC Policy
              </Button>
            </a>
            <p className="text-sky-200/80 text-sm mt-3">
              ✔ Independent review · No forced switching · No spam calls
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar - Punchy */}
      <section ref={statsRef} className="py-10 bg-white border-y border-gray-100" data-testid="stats-section">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: 40000, suffix: "+", label: "Families Helped", color: "text-sky-500" },
              { value: 2200, prefix: "₹", suffix: " Cr", label: "Premiums Reviewed", color: "text-emerald-500" },
              { value: 500, suffix: "+", label: "Google Reviews", color: "text-amber-500" },
              { value: 0, suffix: "", label: "Spam Calls", color: "text-red-500", static: "Zero" },
            ].map((stat, index) => (
              <div 
                key={index}
                className={`${statsVisible ? 'animate-bounce-in' : 'opacity-0'} hover:scale-110 transition-transform duration-300`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <p className={`text-3xl md:text-4xl font-bold ${stat.color}`}>
                  {stat.static || <AnimatedCounter end={stat.value} prefix={stat.prefix} suffix={stat.suffix} />}
                </p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ditto-Style Animated Services Cards */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden" data-testid="services-cards-section">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-64 h-64 bg-sky-50 rounded-full blur-3xl opacity-60"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-amber-50 rounded-full blur-3xl opacity-60"></div>
        </div>

        <div className="container-main relative z-10">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              The Ultimate LIC <span className="text-sky-500">Experience</span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              You don't need multiple agents, apps, or follow-ups.<br/>
              We handle everything — clearly and honestly.
            </p>
          </div>

          {/* Animated Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            
            {/* ROW 1 - CORE PROMISES */}
            
            {/* Card 1 - Professional Advice (CORE) */}
            <div className="group relative animate-slide-in-bottom" style={{ animationDelay: '100ms' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-amber-500 rounded-3xl transform group-hover:scale-105 group-hover:-rotate-2 transition-all duration-500 shadow-xl group-hover:shadow-2xl"></div>
              <div className="relative bg-gradient-to-br from-amber-400 to-amber-500 rounded-3xl p-6 overflow-hidden">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/20 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-white/30 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Professional Advice</h3>
                  <p className="text-white/90 text-sm mb-4">IRDAI-certified experts review your policy and tell you what's best for you.</p>
                  <div className="flex items-center gap-2">
                    <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face" alt="Advisor" className="w-10 h-10 rounded-full border-2 border-white/50 object-cover" />
                    <div className="text-white text-xs">
                      <p className="font-semibold">Free Policy Review</p>
                      <p className="text-white/70">3-min form · Expert reviews in 10</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2 - Honest Policy Review (CORE) */}
            <div className="group relative animate-slide-in-bottom" style={{ animationDelay: '200ms' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-rose-400 to-rose-500 rounded-3xl transform group-hover:scale-105 group-hover:rotate-2 transition-all duration-500 shadow-xl group-hover:shadow-2xl"></div>
              <div className="relative bg-gradient-to-br from-rose-400 to-rose-500 rounded-3xl p-6 overflow-hidden">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-white/20 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white/10 rounded-full animate-pulse" style={{animationDelay: '0.7s'}}></div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-white/30 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm group-hover:scale-110 group-hover:-rotate-12 transition-all duration-300">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Honest Policy Review</h3>
                  <p className="text-white/80 text-xs mb-2">(No Commission Bias)</p>
                  <p className="text-white/90 text-sm mb-4">We tell you the truth — keep, modify, or surrender. Your interest first.</p>
                  <div className="flex gap-2">
                    <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">100% Free</span>
                    <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">No Pressure</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3 - Claim Support (CORE) */}
            <div className="group relative animate-slide-in-bottom" style={{ animationDelay: '300ms' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-sky-400 to-sky-500 rounded-3xl transform group-hover:scale-105 group-hover:-rotate-2 transition-all duration-500 shadow-xl group-hover:shadow-2xl"></div>
              <div className="relative bg-gradient-to-br from-sky-400 to-sky-500 rounded-3xl p-6 overflow-hidden">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/20 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full animate-pulse" style={{animationDelay: '0.9s'}}></div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-white/30 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Claim Support</h3>
                  <p className="text-white/90 text-sm mb-4">End-to-end support when it matters most. Full claim filing assistance.</p>
                  <div className="flex gap-2">
                    <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">24/7 Support</span>
                    <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">No Hidden Fees</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ROW 2 - SUPPORTING FEATURES */}

            {/* Card 4 - Application Assistance (Supporting) */}
            <div className="group relative animate-slide-in-bottom" style={{ animationDelay: '400ms' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-3xl transform group-hover:scale-105 group-hover:rotate-2 transition-all duration-500 shadow-xl group-hover:shadow-2xl"></div>
              <div className="relative bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-3xl p-6 overflow-hidden">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-white/20 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white/10 rounded-full animate-pulse" style={{animationDelay: '1.1s'}}></div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-white/30 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm group-hover:scale-110 group-hover:-rotate-12 transition-all duration-300">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Application Assistance</h3>
                  <p className="text-white/90 text-sm mb-4">Complete documentation support for hassle-free policy purchase.</p>
                  <div className="flex gap-2 flex-wrap">
                    <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">Error-free docs</span>
                    <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">Faster processing</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 5 - Premium Reminders (Supporting) */}
            <div className="group relative animate-slide-in-bottom" style={{ animationDelay: '500ms' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-purple-500 rounded-3xl transform group-hover:scale-105 group-hover:-rotate-2 transition-all duration-500 shadow-xl group-hover:shadow-2xl"></div>
              <div className="relative bg-gradient-to-br from-purple-400 to-purple-500 rounded-3xl p-6 overflow-hidden">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/20 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full animate-pulse" style={{animationDelay: '1.3s'}}></div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-white/30 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    <Calendar className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Premium Reminders</h3>
                  <p className="text-white/90 text-sm mb-4">Never miss a payment. We track and remind you before due dates.</p>
                  <div className="flex items-center gap-2 text-white/90 text-xs">
                    <CheckCircle className="w-4 h-4" />
                    <span>Stay on track, always</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 6 - Instant Policy Help (Human + AI) */}
            <div className="group relative animate-slide-in-bottom" style={{ animationDelay: '600ms' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl transform group-hover:scale-105 group-hover:rotate-2 transition-all duration-500 shadow-xl group-hover:shadow-2xl"></div>
              <div className="relative bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl p-6 overflow-hidden">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-sky-500/20 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-sky-500/10 rounded-full animate-pulse" style={{animationDelay: '1.5s'}}></div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-sky-500/30 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm group-hover:scale-110 group-hover:-rotate-12 transition-all duration-300">
                    <Zap className="w-7 h-7 text-sky-400" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-white">Instant Policy Help</h3>
                    <span className="bg-sky-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Human + AI</span>
                  </div>
                  <p className="text-white/90 text-sm mb-4">Quick answers anytime. Backed by experts. No bots replacing humans.</p>
                  <div className="flex items-center gap-2 text-sky-400 text-xs font-medium">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                    <span>Online Now - Click chat icon below</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section ref={expRef} className="section-padding bg-gray-50" data-testid="experience-section">
        <div className="container-main">
          <div className={`text-center mb-12 ${expVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              How the <span className="text-sky-500">Clarity Check</span> Works
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto mb-4">
              Simple, transparent, zero-pressure. Your decision, always.
            </p>
            <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-700 px-4 py-2 rounded-full text-sm font-medium">
              <Clock className="w-4 h-4" />
              ⏱ 3 minutes to submit · 10 minutes for expert review
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            {experienceSteps.map((item, index) => (
              <div 
                key={index} 
                className={`flex gap-4 md:gap-6 mb-8 last:mb-0 ${expVisible ? 'animate-fade-in-left' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 200}ms` }}
                data-testid={`experience-step-${index}`}
              >
                <div className="relative">
                  <div className="w-14 h-14 bg-sky-500 text-white rounded-2xl flex items-center justify-center font-bold text-xl shadow-lg hover:rotate-6 hover:scale-110 transition-all duration-300 animate-jello cursor-pointer">
                    {item.number}
                  </div>
                  {index < experienceSteps.length - 1 && (
                    <div className="absolute top-16 left-1/2 -translate-x-1/2 w-0.5 h-16 bg-gradient-to-b from-sky-500 to-transparent"></div>
                  )}
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.features.map((feature, i) => (
                      <span 
                        key={i} 
                        className="inline-flex items-center gap-1.5 text-sm bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full hover:bg-emerald-100 hover:scale-105 transition-all cursor-default"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={`text-center mt-12 ${expVisible ? 'animate-fade-in-up delay-600' : 'opacity-0'}`}>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-sky-600 hover:bg-sky-700 text-white h-14 px-10 rounded-xl text-lg font-semibold shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110 shine-effect group">
                🔍 Review My LIC Policy Now
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" />
              </Button>
            </a>
            <p className="text-gray-500 text-sm mt-3">
              ✔ No sales pressure · ✔ No obligation · ✔ Your policy, your choice
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section ref={testRef} className="section-padding" data-testid="testimonials-section">
        <div className="container-main">
          <div className={`text-center mb-10 ${testVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Real Stories, Real Savings
            </h2>
            <div className="flex items-center justify-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400 animate-wiggle" style={{ animationDelay: `${i * 200}ms` }} />
              ))}
              <span className="text-gray-500 ml-2">4.9 from 500+ reviews</span>
            </div>
            <p className="text-gray-400 text-sm">
              These are real LIC policyholders who allowed us to share their experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((item, index) => (
              <div 
                key={index} 
                className={`bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 hover:border-sky-200 group ${testVisible ? 'animate-slide-in-bottom' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 200}ms` }}
                data-testid={`testimonial-${index}`}
              >
                {/* Outcome Pill */}
                <div className="mb-4">
                  <span className="inline-block bg-emerald-50 text-emerald-700 text-sm font-semibold px-3 py-1.5 rounded-full">
                    {item.outcome}
                  </span>
                </div>

                {/* Stars */}
                <div className="flex items-center gap-1 text-amber-400 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current group-hover:animate-wiggle" style={{ animationDelay: `${i * 100}ms` }} />)}
                </div>

                {/* Quote */}
                <p className="text-gray-700 mb-5 leading-relaxed">"{item.quote}"</p>

                {/* Identity */}
                <div className="border-t border-gray-100 pt-4 flex items-center gap-3">
                  {/* Avatar */}
                  <div className="w-11 h-11 bg-sky-100 rounded-full flex items-center justify-center text-sky-600 font-bold text-sm flex-shrink-0">
                    {item.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{item.name}</p>
                    <p className="text-gray-500 text-sm">{item.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA After Testimonials */}
          <div className={`text-center mt-12 ${testVisible ? 'animate-fade-in-up delay-600' : 'opacity-0'}`}>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-sky-600 hover:bg-sky-700 text-white h-14 px-10 rounded-xl text-lg font-semibold shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110 shine-effect group">
                🔍 Review My LIC Policy Now
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" />
              </Button>
            </a>
            <p className="text-gray-500 text-sm mt-3">
              Free · No pressure · IRDAI-certified experts
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Links */}
      <section ref={calcRef} className="section-padding bg-gray-50" data-testid="calculator-section">
        <div className="container-main">
          <div className={`text-center mb-10 ${calcVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Free Planning Tools <span className="text-gray-400 font-normal">(Optional)</span>
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto">
              Helpful calculators — but a human review always catches what tools miss.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { 
                title: "Savings Calculator", 
                desc: "See how ₹5,000/month grows over time", 
                caveat: "Doesn't account for LIC charges or surrender impact",
                link: "/calculator/monthly-saving", 
                icon: TrendingUp, 
                color: "bg-sky-50 text-sky-500" 
              },
              { 
                title: "Retirement Calculator", 
                desc: "Check if you're on track", 
                caveat: "LIC pensions often behave differently than expected",
                link: "/calculator/retirement", 
                icon: Target, 
                color: "bg-emerald-50 text-emerald-500" 
              },
              { 
                title: "Education Planner", 
                desc: "Plan for your child's future", 
                caveat: "Most LIC education plans underperform inflation",
                link: "/calculator/child-education", 
                icon: Heart, 
                color: "bg-amber-50 text-amber-500" 
              },
            ].map((calc, index) => (
              <Link 
                key={index} 
                to={calc.link} 
                className={`bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-xl transition-all duration-500 group hover:-translate-y-3 shine-effect ${calcVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 150}ms` }}
                data-testid={`calculator-link-${index}`}
              >
                <div className={`w-12 h-12 ${calc.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                  <calc.icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-sky-500 transition-colors">{calc.title}</h3>
                <p className="text-gray-500 text-sm mb-2">{calc.desc}</p>
                <p className="text-amber-600 text-xs mb-3 italic">{calc.caveat}</p>
                <span className="text-sky-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  Try now <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-sky-600 via-sky-500 to-emerald-500 relative overflow-hidden" data-testid="final-cta">
        {/* Animated shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-float blur-xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full animate-float blur-xl" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white/10 rounded-full animate-float blur-xl" style={{animationDelay: '2s'}}></div>
          {/* Sparkles */}
          <div className="absolute top-20 right-1/4 w-2 h-2 bg-white rounded-full animate-ping"></div>
          <div className="absolute bottom-20 left-1/4 w-2 h-2 bg-white rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
        </div>
        
        <div className="container-main text-center relative z-10">
          <div className="animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Stop Guessing. Start Knowing.
            </h2>
            <p className="text-sky-100 mb-8 max-w-lg mx-auto text-lg">
              Get your LIC policy reviewed by IRDAI-certified experts. 
              <span className="font-semibold text-white"> 100% Free. Zero Spam.</span>
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-sky-600 hover:bg-sky-50 h-14 px-10 rounded-xl font-bold shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 group">
                🔍 Check My LIC Policy (Free)
              </Button>
            </a>
            <div className="text-center">
              <a href={`tel:${PHONE}`}>
                <Button className="bg-white/20 text-white hover:bg-white/30 h-14 px-10 rounded-xl font-semibold border border-white/30 hover:scale-110 transition-all duration-300">
                  <Phone className="w-5 h-5 mr-2" />
                  📞 Talk to an Expert (Optional)
                </Button>
              </a>
              <p className="text-white/60 text-xs mt-2">☕ 10–15 mins · No pitch · Ask anything</p>
            </div>
          </div>

          <div className="mt-8 animate-fade-in-up delay-500">
            <p className="text-white/80 text-sm mb-2">
              ✔ No pressure · ✔ No obligation · ✔ We don't sell unless it makes sense for you
            </p>
            <p className="text-white/60 text-xs">
              👤 A real expert will review your policy — not an automated report
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
